/**
 * Walks planned in the browser. Asked for on 2026-09-28: "do "map walking"
 * client side so the server doesn't have to calculate it. raise the
 * distance limit to maybe 300 tiles, or on a timer that's short enough the
 * interface doesn't lag".
 *
 * A left click on the ground used to send the tile, and the server searched
 * for a way there on the world's own goroutine, which is why a click was
 * held to forty tiles. Now the search is here: over the map this character
 * has explored, by the rules the server walks by, ported one for one from
 * internal/game/map.go — what can be stood on (Tile.Walkable), what a step
 * costs (terrainCost: two for a straight step and five for a diagonal, in
 * tenths of the tile's pace), where a step onto a passage comes out
 * (StepTarget: stairs, ramps, slopes, holes and ladders), what a change of
 * level is charged (levelChangeCost), and what counts as the end of a walk
 * (the goals in World.walk). The route found goes to the server, which
 * checks each step follows the last and walks it (game/route.go).
 *
 * It runs in slices — so many milliseconds of it a frame — so a search
 * three hundred tiles long never holds the page still; see RoutePlanner.
 * Nothing here touches the page, so the tests run it under node on the
 * real map.
 *
 * Ground never seen is planned over as if it were open grass. Asked for on
 * 2026-09-28: "allow map clicking into unexplored areas". Until then
 * ground never seen was not walked over, since nothing is known of it, and
 * a click out into the black — or on the far side of it — was "There is
 * no way." Now the plan is hopeful about the black: what is known keeps
 * its real price and its walls, and what is not is priced as an ordinary
 * step, a hair dearer than grass so that of two routes otherwise equal the
 * one over known ground is taken. The server walks the route a step at a
 * time, as it walks any, and when the ground turns out to be a wall, water
 * or a hole the plan did not know about, it finds the way on from where
 * the walker stands (game/route.go, scouting). Nothing is learnt here about
 * the black by planning over it; the walk shows it as it goes, as walking
 * always has.
 *
 * Only the ground has a black to be hopeful about. The other levels are
 * sparse — air above, rock below — and what the page has of them is the
 * tiles seen, so a place on them with no tile is as likely rock as unseen;
 * those are planned over as known, which is to say not at all — all but
 * the one tile a stair, a hole or a ladder lets out on, which is ground
 * wherever it is (see landsFixed).
 */
import { Tile } from "./types";

/** A place: a tile of a level. */
export interface Place {
  x: number;
  y: number;
  z: number;
}

/** What the planner needs of the world: the tiles known, level by level. */
export interface PathWorld {
  mapW: number;
  mapH: number;
  /** The tile at (x, y) on level z, or undefined where nothing is known to be. */
  tileOn(z: number, x: number, y: number): number | undefined;
  /**
   * Whether the ground at (x, y) has never been seen: planned over as
   * open ground (see the note at the top). Left out, nothing is.
   */
  unexplored?(x: number, y: number): boolean;
}

/** How far off a click may name a place to walk to, in tiles either way. */
export const CLICK_RANGE = 300;
/** The longest route the server takes (game.maxRouteSteps). */
export const MAX_ROUTE_STEPS = 1000;
/** The most places one plan looks at before it says there is no way. */
export const PLAN_NODES = 400_000;

// Costs, as the server's finder charges them: see map.go.
const TERRAIN_UNITS = 20;
const STEP_COST = 2 * TERRAIN_UNITS;
const DIAG_STEP_COST = 5 * TERRAIN_UNITS;
const LEVEL_CHANGE_COST = 10 * STEP_COST;
/** walkDetour: a route may be twice the straight way, and twenty over. */
const WALK_DETOUR = 2;
/**
 * What a step onto ground never seen is priced at: grass, and one more, so
 * a route over known ground wins a tie. Never less than a road's, which is
 * what the estimate of the rest is priced at, so the estimate still never
 * overestimates.
 */
const UNKNOWN_STEP = STEP_COST + 1;
const UNKNOWN_DIAG = DIAG_STEP_COST + 1;

// Dir order and deltas as geom.go has them: N, E, S, W, then the diagonals.
const DX = [0, 1, 0, -1, 1, 1, -1, -1];
const DY = [-1, 0, 1, 0, -1, 1, 1, -1];
const N = 0, E = 1, S = 2, W = 3;
const RAMP_DIRS = [N, E, S, W];

/** Tile.Walkable: everything but water, walls, trees, rock and palisades. */
export function walkable(t: number | undefined): boolean {
  if (t === undefined) return false;
  switch (t) {
    case Tile.Water:
    case Tile.Wall:
    case Tile.Tree:
    case Tile.Rock:
    case Tile.CaveWall:
    case Tile.Palisade:
    case Tile.Hedge:
    case Tile.Hillside:
    case Tile.HobbitDoor:
    case Tile.MillWheel:
    case Tile.PartyTree:
    case Tile.Ship:
    case Tile.FrozenWaste:
      return false;
  }
  return true;
}

/**
 * The peoples snow does not slow: raceDef.SnowSure in the server's race
 * table (2026-09-29: "implement all the race differences as proposed").
 * A step onto snow costs them what a step onto grass does, and a route is
 * planned for them that way.
 */
export const SNOW_SURE: ReadonlySet<string> = new Set(["elf"]);

/**
 * Tile.SpeedMul: how much longer than grass a step onto t takes — for a
 * walker of `race`, which only snow asks about: see groundMulFor.
 */
export function speedMul(t: number, race?: string): number {
  if (t === Tile.Snow && race && SNOW_SURE.has(race)) return 1;
  switch (t) {
    case Tile.Swamp:
      return 1.8;
    case Tile.Sand:
    case Tile.Hilltop:
      return 1.15;
    case Tile.Snow:
      // snowSpeedMul (2026-09-28: "put snow as the ground on upper levels
      // of the pass at rivendell. movement is slower").
      return 1.5;
    case Tile.Cobble:
    case Tile.Bridge:
      // roadSpeedMul (2026-10-21: "Alter roads and bridges to x0.8").
      return 0.8;
    case Tile.Stone:
    case Tile.Temple:
      return 0.9;
  }
  return 1;
}

/** terrainCost: what the finder charges a walker of `race` for a step onto t. */
export function terrainCost(t: number, diagonal: boolean, race?: string): number {
  const units = Math.floor(speedMul(t, race) * TERRAIN_UNITS + 0.5);
  return ((diagonal ? DIAG_STEP_COST : STEP_COST) / TERRAIN_UNITS) * units;
}

/** The tile of a level, bounds-checked: off the map is nothing. */
function levelAt(w: PathWorld, z: number, x: number, y: number): number | undefined {
  if (x < 0 || y < 0 || x >= w.mapW || y >= w.mapH) return undefined;
  return w.tileOn(z, x, y);
}

/** Whether (x, y, z) is ground never seen, planned over as open ground. */
function unknownAt(w: PathWorld, z: number, x: number, y: number): boolean {
  if (z !== 0 || x < 0 || y < 0 || x >= w.mapW || y >= w.mapH) return false;
  return !!w.unexplored?.(x, y);
}

/** Whether a walk may stand at (x, y, z): ground known to be open, or never seen. */
function openAt(w: PathWorld, z: number, x: number, y: number): boolean {
  return walkable(levelAt(w, z, x, y)) || unknownAt(w, z, x, y);
}

/** Map.TileAt: nothing reads as cave wall, never walkable. */
function tileAt(w: PathWorld, z: number, x: number, y: number): number {
  return levelAt(w, z, x, y) ?? Tile.CaveWall;
}

/**
 * Map.StepTarget: where a step onto (x, y) from level z puts a walker.
 * Every passage lets out a tile off its own spot, so nobody stands on one.
 */
export function stepTarget(w: PathWorld, x: number, y: number, z: number): Place {
  switch (tileAt(w, z, x, y)) {
    case Tile.Stair:
      return { x, y: y - 1, z: z + 1 };
    case Tile.Ramp:
    case Tile.Slope: {
      const d = rampClimbs(w, x, y, z);
      if (d !== undefined) return { x: x + DX[d], y: y + DY[d], z: z + 1 };
      return { x, y: y - 1, z: z + 1 };
    }
    case Tile.RampTop:
    case Tile.SlopeTop: {
      // Down off the far end of the foot under it, in line with the slope
      // (Map.StepTarget, 2026-10-08), and only failing that the first side
      // that lets down.
      const up = rampClimbs(w, x, y, z - 1);
      if (up !== undefined) {
        const d = (up + 2) & 3;
        if (rampLetsDown(w, x + DX[d], y + DY[d], z)) return { x: x + DX[d], y: y + DY[d], z: z - 1 };
      }
      for (const d of RAMP_DIRS) {
        const nx = x + DX[d], ny = y + DY[d];
        if (rampLetsDown(w, nx, ny, z)) return { x: nx, y: ny, z: z - 1 };
      }
      return { x, y: y + 1, z: z - 1 };
    }
    case Tile.StairTop:
    case Tile.Hole:
    case Tile.LadderTop:
      return { x, y: y + 1, z: z - 1 };
    case Tile.Ladder:
      return { x, y: y - 1, z: z + 1 };
  }
  return { x, y, z };
}

/** Map.rampClimbs: the way the foot of a ramp or slope at (x, y, z) goes up. */
function rampClimbs(w: PathWorld, x: number, y: number, z: number): number | undefined {
  const t = levelAt(w, z, x, y);
  if (t !== Tile.Ramp && t !== Tile.Slope) return undefined;
  for (const d of RAMP_DIRS) {
    const nx = x + DX[d], ny = y + DY[d];
    if (tileAt(w, z, nx, ny) === Tile.Rock && walkable(levelAt(w, z + 1, nx, ny))) return d;
  }
  return undefined;
}

/** Map.rampLetsDown: whether a ramp's head on level z can let a walker down onto (x, y). */
function rampLetsDown(w: PathWorld, x: number, y: number, z: number): boolean {
  return levelAt(w, z, x, y) === undefined && walkable(levelAt(w, z - 1, x, y));
}

/**
 * Whether a step onto t lets out on one place whatever is round it: a
 * stair, a hole or a ladder, at either end, always puts a walker on the
 * same tile beside it a level up or down (stepTarget): north of a stair
 * or of a ladder's head going up, south of a hole or a stair's head going
 * down. A ramp or a slope picks its
 * way by the levels round it, which a page that has not seen them cannot
 * answer.
 *
 * Those are the passages whose far end is trusted unseen. Reported on
 * 2026-10-02: "Users on safari/iPhone are reporting they can't click on
 * ladders to move up or down a level". The page plans over what it has
 * seen, and under the ground a character sees only the floor it stands on
 * (explore.go), so the level a hole or a hatch goes down to, or a ladder
 * up to, was nothing at all until the character had been there: the
 * landing was no ground to stand on, the plan had nowhere to end, and a
 * tap on the ladder said "There is no way." and moved nobody. A key, or a
 * finger dragged, steps onto the ladder without a plan, which is why it
 * was the phones that noticed: a tap is how a phone walks. (A click was the
 * server's walk until the page planned it, and the server knows every
 * level.) No passage in the world lets out onto anything that cannot be
 * stood on (TestPassageExitsAreClearOfFixtures), so the landing is planned
 * as ground never seen, at an ordinary step's price, and the server, which
 * checks every step of a route against the real map (game/route.go),
 * walks it down or up. Only the landing: nothing past it on that level is
 * planned over, so a click on this level is not walked to by way of a cave
 * nobody has seen.
 */
function landsFixed(t: number): boolean {
  switch (t) {
    case Tile.Stair:
    case Tile.StairTop:
    case Tile.Hole:
    case Tile.LadderTop:
    case Tile.Ladder:
      return true;
  }
  return false;
}

/** Whether (x, y) is on the map at all. */
function onMap(w: PathWorld, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < w.mapW && y < w.mapH;
}

/** Whether a step onto t goes through it to somewhere else (see stepTarget). */
function isPassage(t: number): boolean {
  switch (t) {
    case Tile.Stair:
    case Tile.Ramp:
    case Tile.Slope:
    case Tile.RampTop:
    case Tile.SlopeTop:
    case Tile.StairTop:
    case Tile.Hole:
    case Tile.LadderTop:
    case Tile.Ladder:
      return true;
  }
  return false;
}

/**
 * What a plan knows of the places it has reached: the cheapest way found
 * to each, where it came from, and whether it is settled.
 *
 * The ground is a million tiles and a long plan reaches hundreds of
 * thousands of them, which in Maps and Sets was a hash table grown and
 * rehashed over and over, and the pauses for that were frames the page
 * missed. So the ground's are flat arrays a tile each, made once and kept
 * between plans, with a stamp saying which plan wrote each entry, so a
 * new plan starts clean without clearing a million of anything. The
 * levels off the ground are small and stay in Maps. Only the newest plan
 * may use the arrays; an older one still held finds itself stale and
 * gives up.
 */
interface GroundScratch {
  size: number;
  gen: number;
  best: Float64Array;
  prev: Int32Array;
  seen: Uint32Array;
  done: Uint32Array;
}
let scratch: GroundScratch | null = null;

class Marks {
  readonly gen: number;
  private readonly s: GroundScratch;
  private readonly base: number;
  private readonly n: number;
  private readonly bestM = new Map<number, number>();
  private readonly prevM = new Map<number, number>();
  private readonly doneM = new Set<number>();

  constructor(W: number, H: number) {
    this.n = W * H;
    this.base = 16 * this.n; // the key of (0, 0) on the ground: see RoutePlanner.key
    if (!scratch || scratch.size !== this.n || scratch.gen >= 0xfffffffe) {
      scratch = {
        size: this.n,
        gen: 0,
        best: new Float64Array(this.n),
        prev: new Int32Array(this.n),
        seen: new Uint32Array(this.n),
        done: new Uint32Array(this.n),
      };
    }
    this.s = scratch;
    this.gen = ++scratch.gen;
  }

  get stale(): boolean {
    return scratch !== this.s || this.s.gen !== this.gen;
  }

  best(k: number): number | undefined {
    const i = k - this.base;
    if (i >= 0 && i < this.n) return this.s.seen[i] === this.gen ? this.s.best[i] : undefined;
    return this.bestM.get(k);
  }

  prev(k: number): number {
    const i = k - this.base;
    if (i >= 0 && i < this.n) return this.s.prev[i];
    return this.prevM.get(k)!;
  }

  reach(k: number, cost: number, from: number) {
    const i = k - this.base;
    if (i >= 0 && i < this.n) {
      this.s.seen[i] = this.gen;
      this.s.best[i] = cost;
      this.s.prev[i] = from;
      return;
    }
    this.bestM.set(k, cost);
    this.prevM.set(k, from);
  }

  done(k: number): boolean {
    const i = k - this.base;
    if (i >= 0 && i < this.n) return this.s.done[i] === this.gen;
    return this.doneM.has(k);
  }

  settle(k: number) {
    const i = k - this.base;
    if (i >= 0 && i < this.n) this.s.done[i] = this.gen;
    else this.doneM.add(k);
  }
}

/** What a plan is asked: from where, to which tile of the walker's own level. */
export interface PlanAsk {
  from: Place;
  to: { x: number; y: number };
  /** Somebody or something solid is standing there: a creature, a chest, a shut door. */
  blocked?: (x: number, y: number, z: number) => boolean;
  /** Ground shut to this walker: a protection zone, to somebody with a skull. */
  shut?: (x: number, y: number, z: number) => boolean;
  /** The walker's people, which decides what snow costs them (see SNOW_SURE). */
  race?: string;
}

/** Where a plan is: still looking, found a route, or found there is none. */
export type PlanState = "searching" | "found" | "none";

/**
 * One plan, run a slice at a time. It is an A* over places, cheapest route
 * first exactly as the server's findPath is, with the server's own
 * estimate of what is left: the straight steps to the click at a road's
 * price. That is the tightest estimate that never overestimates here,
 * because a diagonal costs two and a half straight steps and so is never
 * cheaper than going round by two — which makes the octile distance, the
 * usual estimate on eight-way ground, the same as the straight one. And it
 * is held to the server's cap on a walk's length (walkDetour), which is
 * what ends a search for somewhere there is no way to.
 */
export class RoutePlanner {
  state: PlanState = "searching";
  /** The route, once found: every place stepped through, the start left out. */
  route: Place[] = [];
  /** How many places have been looked at. */
  expanded = 0;

  private readonly W: number;
  private readonly H: number;
  private readonly goals = new Set<number>();
  private readonly marks: Marks;
  // The frontier: a binary heap over parallel arrays, cheapest first and
  // first pushed first among equals, as the server's pathQueue.
  private hk: number[] = [];
  private hc: number[] = [];
  private hp: number[] = [];
  private hs: number[] = [];
  private seq = 0;
  private readonly maxCost: number;
  private readonly start: number;

  constructor(private readonly w: PathWorld, private readonly ask: PlanAsk) {
    this.W = w.mapW;
    this.H = w.mapH;
    this.marks = new Marks(this.W, this.H);
    const { from, to } = ask;
    const z = from.z;
    const shut = (x: number, y: number, zz: number) => !!ask.shut?.(x, y, zz);
    const blocked = (x: number, y: number, zz: number) => !!ask.blocked?.(x, y, zz);
    const standable = (x: number, y: number, zz: number) => openAt(w, zz, x, y) && !blocked(x, y, zz);
    this.start = this.key(from.x, from.y, from.z);
    this.maxCost = STEP_COST * (WALK_DETOUR * (Math.abs(to.x - from.x) + Math.abs(to.y - from.y)) + 20) + 2 * LEVEL_CHANGE_COST;
    if (to.x < 0 || to.y < 0 || to.x >= this.W || to.y >= this.H || shut(to.x, to.y, z)) {
      this.state = "none";
      return;
    }
    // The goals, as World.walk has them: the clicked tile on this level,
    // or where a passage clicked lets out; and beside the tile when it
    // cannot be stood on — a creature, a chest, a wall, water — which is
    // also where it is used from. Ground never seen is taken to be ground
    // to stand on: the click is on the tile, and if it turns out to be
    // water the server walks up to it and says so.
    if (standable(to.x, to.y, z)) {
      this.goals.add(this.key(to.x, to.y, z));
      const far = stepTarget(w, to.x, to.y, z);
      if (!shut(far.x, far.y, far.z)) this.goals.add(this.key(far.x, far.y, far.z));
    } else {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = to.x + dx, y = to.y + dy;
          if ((dx || dy) && standable(x, y, z) && !shut(x, y, z)) this.goals.add(this.key(x, y, z));
        }
      }
    }
    // Nowhere to end: the open sea, or a level's gap. Said at once
    // rather than after looking at everything within reach.
    if (this.goals.size === 0) {
      this.state = "none";
      return;
    }
    if (this.goals.has(this.start)) {
      this.state = "found";
      return;
    }
    this.marks.reach(this.start, 0, this.start);
    this.push(this.start, 0, 0);
  }

  /** A place as one number: level, row and column. */
  private key(x: number, y: number, z: number): number {
    return ((z + 16) * this.H + y) * this.W + x;
  }

  private place(k: number): Place {
    const x = k % this.W;
    const r = (k - x) / this.W;
    const y = r % this.H;
    return { x, y, z: (r - y) / this.H - 16 };
  }

  private h(x: number, y: number): number {
    const steps = Math.abs(x - this.ask.to.x) + Math.abs(y - this.ask.to.y) - 2;
    return Math.max(steps, 0) * terrainCost(Tile.Cobble, false);
  }

  /**
   * Look at up to `nodes` more places, or until `until` on `clock` (in
   * milliseconds) has passed, whichever comes first, and say where the
   * plan is. Call again while it says "searching".
   */
  work(nodes = PLAN_NODES, until = Infinity, clock: () => number = () => 0): PlanState {
    const { w } = this;
    const blocked = this.ask.blocked;
    const shut = this.ask.shut;
    const marks = this.marks;
    if (marks.stale) this.state = "none";
    const W = this.W, H = this.H;
    let n = 0;
    while (this.state === "searching") {
      if (this.hk.length === 0 || this.expanded >= PLAN_NODES) {
        this.state = "none";
        break;
      }
      if (n >= nodes || ((n & 63) === 63 && clock() >= until)) break;
      const k = this.pop();
      const cost = this.popped;
      if (marks.done(k)) continue;
      marks.settle(k);
      this.expanded++;
      n++;
      if (this.goals.has(k)) {
        const route: Place[] = [];
        for (let p = k; p !== this.start; p = marks.prev(p)) route.push(this.place(p));
        this.route = route.reverse();
        this.state = "found";
        break;
      }
      const ax = k % W;
      const r = (k - ax) / W;
      const ay = r % H;
      const az = (r - ay) / H - 16;
      for (let d = 0; d < 8; d++) {
        // Where the step lands: the tile itself, unless it is a passage,
        // which is the only case that has to ask stepTarget — and the only
        // one that makes an object, which a million steps would otherwise
        // each have done.
        let tx = ax + DX[d], ty = ay + DY[d], tz = az;
        let tile = levelAt(w, tz, tx, ty);
        let blind = false;
        if (tile !== undefined && isPassage(tile)) {
          blind = landsFixed(tile);
          const t = stepTarget(w, tx, ty, tz);
          tx = t.x;
          ty = t.y;
          tz = t.z;
          tile = levelAt(w, tz, tx, ty);
        }
        // Ground never seen is open ground at an ordinary price: see the
        // note at the top. It is never a passage, since nothing is known
        // to be cut into it. And where a stair, a hole or a ladder lets out
        // on a place never seen, that one place is ground to stand on too
        // (2026-10-02: "Users on safari/iPhone are reporting they can't
        // click on ladders to move up or down a level"): see landsFixed.
        let c: number;
        if (walkable(tile)) c = cost + terrainCost(tile!, d >= 4, this.ask.race);
        else if (tile === undefined && (unknownAt(w, tz, tx, ty) || (blind && onMap(w, tx, ty)))) c = cost + (d >= 4 ? UNKNOWN_DIAG : UNKNOWN_STEP);
        else continue;
        const nk = ((tz + 16) * H + ty) * W + tx;
        if (marks.done(nk)) continue;
        if (tz !== az) c += LEVEL_CHANGE_COST;
        const old = marks.best(nk);
        if (old !== undefined && old <= c) continue;
        // The goal may have somebody on it; nothing else may.
        const goal = this.goals.has(nk);
        if (!goal && (blocked?.(tx, ty, tz) || shut?.(tx, ty, tz))) continue;
        const prio = c + this.h(tx, ty);
        if (prio > this.maxCost) continue;
        marks.reach(nk, c, k);
        this.push(nk, c, prio);
      }
    }
    return this.state;
  }

  private less(i: number, j: number): boolean {
    return this.hp[i] !== this.hp[j] ? this.hp[i] < this.hp[j] : this.hs[i] < this.hs[j];
  }

  private swap(i: number, j: number) {
    const { hk, hc, hp, hs } = this;
    let t = hk[i];
    hk[i] = hk[j];
    hk[j] = t;
    t = hc[i];
    hc[i] = hc[j];
    hc[j] = t;
    t = hp[i];
    hp[i] = hp[j];
    hp[j] = t;
    t = hs[i];
    hs[i] = hs[j];
    hs[j] = t;
  }

  private push(k: number, cost: number, prio: number) {
    this.hk.push(k);
    this.hc.push(cost);
    this.hp.push(prio);
    this.hs.push(this.seq++);
    let i = this.hk.length - 1;
    while (i > 0) {
      const up = (i - 1) >> 1;
      if (!this.less(i, up)) break;
      this.swap(i, up);
      i = up;
    }
  }

  /** The cost of what pop last returned. */
  private popped = 0;

  private pop(): number {
    const out = this.hk[0];
    this.popped = this.hc[0];
    const last = this.hk.length - 1;
    this.swap(0, last);
    this.hk.pop();
    this.hc.pop();
    this.hp.pop();
    this.hs.pop();
    let i = 0;
    for (;;) {
      const l = 2 * i + 1, r = l + 1;
      let m = i;
      if (l < last && this.less(l, m)) m = l;
      if (r < last && this.less(r, m)) m = r;
      if (m === i) break;
      this.swap(i, m);
      i = m;
    }
    return out;
  }
}

/** A whole plan at once, for the tests: the route, or null for no way. */
export function planRoute(w: PathWorld, ask: PlanAsk): Place[] | null {
  const p = new RoutePlanner(w, ask);
  return p.work() === "found" ? p.route : null;
}

/** A route as the server's "route" message carries it: x, y and level, a place at a time. */
export function routeSteps(route: Place[]): number[] {
  const out: number[] = [];
  for (const p of route) out.push(p.x, p.y, p.z);
  return out;
}
